import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db514phuu2cnvnf27gc0';

export default function Newsletter() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // 统一从表单元素读取数据，避免与自动填充不同步
    const data = new FormData(form);
    const honeypot = String(data.get('website_alt') ?? '').trim();

    if (honeypot) {
      // 命中蜜罐：对外表现与正常提交一致，绝不发送到表单地址
      setStatus('success');
      setMessage('订阅成功，下一期片单会优先发到你的邮箱。');
      form.reset();
      return;
    }

    data.delete('website_alt');

    setStatus('submitting');
    setMessage('正在提交…');

    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, String(value)));

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setMessage('订阅成功，下一期片单会优先发到你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交没有成功，请稍后再试一次。');
      }
    } catch {
      setStatus('error');
      setMessage('网络似乎开小差了，请稍后再试一次。');
    }
  };

  return (
    <section id="subscribe" className="bg-primary-50 border-y border-primary-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50 px-3 py-1 text-xs font-medium tracking-wide text-primary-800">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              片单订阅
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              把好看的电影，定期送到你邮箱
            </h2>
            <p className="mt-2 text-sm md:text-base leading-relaxed text-foreground-600">
              留下邮箱和你想看的类型，我们会在每次更新片单时整理一份最新电影与热门影片清单发给你，不再需要自己一部部翻找。
            </p>
          </div>

          <form
            id="jinju-subscribe-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-xl border border-primary-100 bg-background-50 p-5 md:p-6"
          >
            <div className="flex flex-col gap-4">
              <div>
                <label htmlFor="sub-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  邮箱地址
                </label>
                <input
                  id="sub-email"
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="sub-preference" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  想接收的片单方向
                </label>
                <select
                  id="sub-preference"
                  name="preference"
                  defaultValue="all"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none cursor-pointer"
                >
                  <option value="all">全部都发</option>
                  <option value="latest">最新电影</option>
                  <option value="hot">热门影片</option>
                  <option value="hidden">冷门宝藏</option>
                  <option value="ranking">电影排行榜解读</option>
                </select>
              </div>

              <div className="jinju-alt-field" aria-hidden="true">
                <label htmlFor="sub-site-alt">请勿填写此项</label>
                <input
                  id="sub-site-alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-send-line text-base"></i>
                </span>
                {status === 'submitting' ? '提交中…' : '订阅片单'}
              </button>

              {message ? (
                <p className={`text-xs ${status === 'error' ? 'text-accent-700' : 'text-secondary-700'}`}>{message}</p>
              ) : (
                <p className="text-xs text-foreground-400">我们只用来发送片单，不会分享给第三方。</p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}